// Must stay in sync with the invite URL in docs.quickchat.ai/channels/discord.
export const DISCORD_INVITE_PERMISSIONS = '309237712896';

const SNOWFLAKE_PATTERN = /^\d{15,21}$/;
// A real first segment is base64 of a <=21-digit snowflake (<=28 chars); cap well above that so a giant pasted value never reaches atob on every keystroke.
const MAX_SEGMENT_LENGTH = 64;

// The first dot-separated token segment is base64(bot user id); for modern bots the bot user id equals the application id.
export const deriveDiscordApplicationId = (token: string): string | null => {
  const firstSegment = token
    .trim()
    .replace(/^Bot\s+/i, '')
    .split('.')[0];
  if (!firstSegment || firstSegment.length > MAX_SEGMENT_LENGTH) return null;
  const normalized = firstSegment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  try {
    const decoded = atob(padded);
    return SNOWFLAKE_PATTERN.test(decoded) ? decoded : null;
  } catch {
    return null;
  }
};

export const buildDiscordInviteUrl = (applicationId: string): string =>
  `https://discord.com/api/oauth2/authorize?client_id=${applicationId}&permissions=${DISCORD_INVITE_PERMISSIONS}&scope=bot`;

export const buildDiscordBotSettingsUrl = (applicationId: string): string =>
  `https://discord.com/developers/applications/${applicationId}/bot`;

// Discord OAuth2 client secrets are ~32 url-safe chars with no dots; bot tokens always have two.
const CLIENT_SECRET_PATTERN = /^[A-Za-z0-9_-]{25,50}$/;

// The wrong-paste shapes users actually hit (per the setup-failure analysis):
// the Application ID, the Client Secret, or a copied portal/invite URL. Only
// these three are treated as definitive; everything else stays submittable so
// the backend getMe check remains the sole authority.
export type DiscordTokenInputClassification =
  | 'token_like'
  | 'application_id'
  | 'client_secret'
  | 'url'
  | 'unrecognized';

export const classifyDiscordTokenInput = (raw: string): DiscordTokenInputClassification => {
  const value = raw.trim().replace(/^Bot\s+/i, '');
  if (/^https?:\/\//i.test(value) || value.toLowerCase().includes('discord.com/')) return 'url';
  if (SNOWFLAKE_PATTERN.test(value)) return 'application_id';
  if (deriveDiscordApplicationId(value) !== null) return 'token_like';
  if (!value.includes('.') && CLIENT_SECRET_PATTERN.test(value)) return 'client_secret';
  return 'unrecognized';
};
